import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  UserIcon, 
  CogIcon, 
  CreditCardIcon, 
  ShieldCheckIcon,
  BellIcon,
  TrashIcon,
  SunIcon,
  MoonIcon
} from '@heroicons/react/24/outline';
import Navbar from './Navbar';
import { useTheme } from '../contexts/ThemeContext';

const AccountPage = () => {
  const [activeTab, setActiveTab] = useState('profile');
  const navigate = useNavigate();
  const { theme, setLightTheme, setDarkTheme, isDark } = useTheme();
  const [profileData, setProfileData] = useState({
    firstName: 'John',
    lastName: 'Doe',
    email: 'john.doe@example.com',
    company: 'Tech Corp',
    phone: '+1 (555) 123-4567',
    timezone: 'UTC-5'
  });

  const [notifications, setNotifications] = useState({
    emailUpdates: true,
    securityAlerts: true,
    marketingEmails: false,
    workflowNotifications: true
  });

  const handleProfileChange = (e) => {
    setProfileData({
      ...profileData,
      [e.target.name]: e.target.value
    });
  };

  const handleNotificationChange = (key) => {
    setNotifications({
      ...notifications,
      [key]: !notifications[key]
    });
  };

  const tabs = [
    { id: 'profile', name: 'Profile', icon: UserIcon },
    { id: 'security', name: 'Security', icon: ShieldCheckIcon },
    { id: 'billing', name: 'Billing', icon: CreditCardIcon },
    { id: 'notifications', name: 'Notifications', icon: BellIcon },
    { id: 'preferences', name: 'Preferences', icon: CogIcon }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-dark transition-colors duration-200">
      {/* Header */}
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-black dark:text-white">Account Settings</h1>
          <p className="text-gray-500 dark:text-gray-300 mt-2">Manage your account preferences and settings</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar */}
          <div className="lg:w-64">
            <nav className="space-y-2">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-colors ${
                      activeTab === tab.id
                        ? 'bg-primary text-white'
                        : 'text-gray-500 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-black dark:hover:text-white'
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                    <span>{tab.name}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700 transition-colors duration-200">
              {/* Profile Tab */}
              {activeTab === 'profile' && (
                <div>
                  <h2 className="text-2xl font-bold text-black dark:text-white mb-6">Profile Information</h2>
                  <form className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-500 dark:text-gray-300 mb-2">
                          First Name
                        </label>
                        <input
                          type="text"
                          name="firstName"
                          value={profileData.firstName}
                          onChange={handleProfileChange}
                          className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-primary transition-colors duration-200"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500 dark:text-gray-300 mb-2">
                          Last Name
                        </label>
                        <input
                          type="text"
                          name="lastName"
                          value={profileData.lastName}
                          onChange={handleProfileChange}
                          className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-primary transition-colors duration-200"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-500 dark:text-gray-300 mb-2">
                        Email Address
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={profileData.email}
                        onChange={handleProfileChange}
                        className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-primary transition-colors duration-200"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-500 dark:text-gray-300 mb-2">
                          Company
                        </label>
                        <input
                          type="text"
                          name="company"
                          value={profileData.company}
                          onChange={handleProfileChange}
                          className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-primary transition-colors duration-200"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500 dark:text-gray-300 mb-2">
                          Phone
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          value={profileData.phone}
                          onChange={handleProfileChange}
                          className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-primary transition-colors duration-200"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-500 dark:text-gray-300 mb-2">
                        Timezone
                      </label>
                      <select
                        name="timezone"
                        value={profileData.timezone}
                        onChange={handleProfileChange}
                        className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-primary transition-colors duration-200"
                      >
                        <option value="UTC-8">Pacific Time (UTC-8)</option>
                        <option value="UTC-7">Mountain Time (UTC-7)</option>
                        <option value="UTC-6">Central Time (UTC-6)</option>
                        <option value="UTC-5">Eastern Time (UTC-5)</option>
                        <option value="UTC+0">UTC</option>
                      </select>
                    </div>

                    <button
                      type="submit"
                      className="bg-primary text-white px-6 py-2 rounded-md hover:bg-blue-600 transition duration-200"
                    >
                      Save Changes
                    </button>
                  </form>
                </div>
              )}

              {/* Security Tab */}
              {activeTab === 'security' && (
                <div>
                  <h2 className="text-2xl font-bold text-black dark:text-white mb-6">Security Settings</h2>
                  <div className="space-y-6">
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-700 transition-colors duration-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-lg font-semibold text-black dark:text-white">Change Password</h3>
                          <p className="text-gray-500 dark:text-gray-300">Update your account password</p>
                        </div>
                        <button className="bg-primary text-white px-4 py-2 rounded-md hover:bg-blue-600 transition-colors duration-200">
                          Change
                        </button>
                      </div>
                    </div>

                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-700 transition-colors duration-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-lg font-semibold text-black dark:text-white">Two-Factor Authentication</h3>
                          <p className="text-gray-500 dark:text-gray-300">Add an extra layer of security to your account</p>
                        </div>
                        <button className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors duration-200">
                          Enable
                        </button>
                      </div>
                    </div>

                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-700 transition-colors duration-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-lg font-semibold text-black dark:text-white">API Keys</h3>
                          <p className="text-gray-500 dark:text-gray-300">Manage your API access keys</p>
                        </div>
                        <button className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 transition-colors duration-200">
                          Manage
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Billing Tab */}
              {activeTab === 'billing' && (
                <div>
                  <h2 className="text-2xl font-bold text-black dark:text-white mb-6">Billing & Subscription</h2>
                  <div className="space-y-6">
                    <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-6 transition-colors duration-200">
                      <h3 className="text-xl font-semibold text-black dark:text-white mb-4">Current Plan</h3>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-2xl font-bold text-primary">Pro Plan</p>
                          <p className="text-gray-500 dark:text-gray-300">$29/month • Billed monthly</p>
                        </div>
                        <button 
                          onClick={() => navigate('/pricing')}
                          className="bg-primary text-white px-4 py-2 rounded-md hover:bg-blue-600 transition-colors duration-200"
                        >
                          Upgrade
                        </button>
                      </div>
                    </div>

                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-700 transition-colors duration-200">
                      <h3 className="text-lg font-semibold text-black dark:text-white mb-4">Payment Method</h3>
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-8 bg-blue-600 rounded flex items-center justify-center">
                          <span className="text-white text-xs font-bold">VISA</span>
                        </div>
                        <div>
                          <p className="text-black dark:text-white">•••• •••• •••• 4242</p>
                          <p className="text-gray-500 dark:text-gray-300 text-sm">Expires 12/25</p>
                        </div>
                        <button className="ml-auto text-primary hover:text-blue-400 transition-colors duration-200">
                          Update
                        </button>
                      </div>
                    </div>

                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-700 transition-colors duration-200">
                      <h3 className="text-lg font-semibold text-black dark:text-white mb-4">Billing History</h3>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-500 dark:text-gray-300">Jan 2024 - Pro Plan</span>
                          <span className="text-black dark:text-white">$29.00</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-500 dark:text-gray-300">Dec 2023 - Pro Plan</span>
                          <span className="text-black dark:text-white">$29.00</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Notifications Tab */}
              {activeTab === 'notifications' && (
                <div>
                  <h2 className="text-2xl font-bold text-black dark:text-white mb-6">Notification Preferences</h2>
                  <div className="space-y-6">
                    {Object.entries(notifications).map(([key, value]) => (
                      <div key={key} className="flex items-center justify-between py-4 border-b border-gray-200 dark:border-gray-700">
                        <div>
                          <h3 className="text-lg font-semibold text-black dark:text-white capitalize">
                            {key.replace(/([A-Z])/g, ' $1').trim()}
                          </h3>
                          <p className="text-gray-500 dark:text-gray-300">
                            {key === 'emailUpdates' && 'Receive updates about your account via email'}
                            {key === 'securityAlerts' && 'Get notified about security-related activities'}
                            {key === 'marketingEmails' && 'Receive promotional emails and product updates'}
                            {key === 'workflowNotifications' && 'Get notified when workflows complete or fail'}
                          </p>
                        </div>
                        <button
                          onClick={() => handleNotificationChange(key)}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            value ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              value ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Preferences Tab */}
              {activeTab === 'preferences' && (
                <div>
                  <h2 className="text-2xl font-bold text-black dark:text-white mb-6">Account Preferences</h2>
                  <div className="space-y-6">
                    {/* Theme Selection */}
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-6 bg-gray-50 dark:bg-gray-700 transition-colors duration-200">
                      <h3 className="text-lg font-semibold text-black dark:text-white mb-4 flex items-center gap-2">
                        <span className="material-symbols-outlined">palette</span>
                        Theme Preference
                      </h3>
                      <p className="text-gray-500 dark:text-gray-300 text-sm mb-4">
                        Choose your preferred theme for the OrbitMind interface
                      </p>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Light Theme Option */}
                        <button
                          onClick={setLightTheme}
                          className={`relative p-4 rounded-lg border-2 transition-all duration-200 ${
                            theme === 'light'
                              ? 'border-primary bg-blue-50 dark:bg-blue-900'
                              : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                          }`}
                        >
                          <div className="flex items-center gap-3 mb-3">
                            <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                              <SunIcon className="h-5 w-5 text-yellow-600" />
                            </div>
                            <div className="text-left">
                              <h4 className="font-semibold text-black dark:text-white">Light Mode</h4>
                              <p className="text-sm text-gray-500 dark:text-gray-300">Clean and bright interface</p>
                            </div>
                          </div>
                          
                          {/* Theme Preview */}
                          <div className="bg-white border border-gray-200 rounded-md p-3 shadow-sm">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                              <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                              <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                            </div>
                            <div className="space-y-1">
                              <div className="h-2 bg-gray-200 rounded w-3/4"></div>
                              <div className="h-2 bg-gray-100 rounded w-1/2"></div>
                              <div className="h-2 bg-gray-200 rounded w-2/3"></div>
                            </div>
                          </div>
                          
                          {theme === 'light' && (
                            <div className="absolute top-2 right-2">
                              <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                                <span className="material-symbols-outlined text-white text-sm">check</span>
                              </div>
                            </div>
                          )}
                        </button>

                        {/* Dark Theme Option */}
                        <button
                          onClick={setDarkTheme}
                          className={`relative p-4 rounded-lg border-2 transition-all duration-200 ${
                            theme === 'dark'
                              ? 'border-primary bg-blue-50 dark:bg-blue-900'
                              : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                          }`}
                        >
                          <div className="flex items-center gap-3 mb-3">
                            <div className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center">
                              <MoonIcon className="h-5 w-5 text-gray-300" />
                            </div>
                            <div className="text-left">
                              <h4 className="font-semibold text-black dark:text-white">Dark Mode</h4>
                              <p className="text-sm text-gray-500 dark:text-gray-300">Easy on the eyes</p>
                            </div>
                          </div>
                          
                          {/* Theme Preview */}
                          <div className="bg-gray-800 border border-gray-700 rounded-md p-3 shadow-sm">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                              <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                              <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                            </div>
                            <div className="space-y-1">
                              <div className="h-2 bg-gray-600 rounded w-3/4"></div>
                              <div className="h-2 bg-gray-700 rounded w-1/2"></div>
                              <div className="h-2 bg-gray-600 rounded w-2/3"></div>
                            </div>
                          </div>
                          
                          {theme === 'dark' && (
                            <div className="absolute top-2 right-2">
                              <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                                <span className="material-symbols-outlined text-white text-sm">check</span>
                              </div>
                            </div>
                          )}
                        </button>
                      </div>
                      
                      <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900 border border-blue-200 dark:border-blue-800 rounded-lg">
                        <p className="text-sm text-blue-800 dark:text-blue-200">
                          <span className="material-symbols-outlined text-sm mr-1">info</span>
                          Your theme preference is automatically saved and will be applied across all OrbitMind pages.
                        </p>
                      </div>
                    </div>

                    {/* Language Selection */}
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-700 transition-colors duration-200">
                      <h3 className="text-lg font-semibold text-black dark:text-white mb-4 flex items-center gap-2">
                        <span className="material-symbols-outlined">language</span>
                        Language
                      </h3>
                      <select className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-black dark:text-white transition-colors duration-200">
                        <option>English</option>
                        <option>Spanish</option>
                        <option>French</option>
                        <option>German</option>
                      </select>
                    </div>

                    {/* Danger Zone */}
                    <div className="border border-red-300 dark:border-red-700 rounded-lg p-4 bg-red-50 dark:bg-red-900 transition-colors duration-200">
                      <h3 className="text-lg font-semibold text-red-600 dark:text-red-400 mb-4 flex items-center gap-2">
                        <span className="material-symbols-outlined">warning</span>
                        Danger Zone
                      </h3>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-black dark:text-white font-medium">Delete Account</p>
                            <p className="text-gray-500 dark:text-gray-300 text-sm">Permanently delete your account and all data</p>
                          </div>
                          <button className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 flex items-center space-x-2 transition-colors duration-200">
                            <TrashIcon className="h-4 w-4" />
                            <span>Delete</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountPage;